import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  Lock,
  KeyRound,
  ShieldCheck,
  X,
  AlertCircle,
  Mail,
  ArrowRight,
  Shield,
} from 'lucide-react';
import { romanticAudio } from '../../utils/audio';
import { signInAdmin } from '../../services/authService';
import { MyWebRequest } from '../../types';

interface UserWebPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (profile: 'novio' | 'novia') => void;
  coupleTitle?: string;
  orderCode?: string;
  partner1Email?: string;
  partner2Email?: string;
  partner1Name?: string;
  partner2Name?: string;
  assignedPassword?: string;
  requestId?: string;
  onRequestUpdate?: (updatedFields: Partial<MyWebRequest>) => Promise<void>;
}

export const UserWebPinModal: React.FC<UserWebPinModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  coupleTitle,
  orderCode,
  partner1Email,
  partner2Email,
  partner1Name,
  partner2Name,
  assignedPassword,
  requestId,
  onRequestUpdate,
}) => {
  const registeredEmail = (partner1Email || partner2Email || '').trim().toLowerCase();
  const [emailInput, setEmailInput] = useState(registeredEmail);
  const [passwordInput, setPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If no assigned password exists yet, default to assigning a password
  const hasExistingPassword = Boolean(assignedPassword && assignedPassword.trim().length >= 6);
  const [isAssigningPassword, setIsAssigningPassword] = useState(!hasExistingPassword);

  if (!isOpen) return null;

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const targetEmail = (emailInput.trim() || registeredEmail).toLowerCase();
    if (!targetEmail || !targetEmail.includes('@')) {
      setError('Por favor introduce un correo electrónico válido.');
      return;
    }

    if (passwordInput.trim().length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (isAssigningPassword) {
      if (passwordInput !== confirmPasswordInput) {
        setError('Las contraseñas no coinciden. Por favor verifícalas.');
        return;
      }

      setIsSubmitting(true);
      try {
        const cleanPassword = passwordInput.trim();
        // 1. Update in request via parent handler
        if (onRequestUpdate) {
          const updateFields: Partial<MyWebRequest> = {
            assignedPassword: cleanPassword,
            email: targetEmail,
            partner1Email: targetEmail,
          };
          await onRequestUpdate(updateFields);
        }

        // 2. Call server endpoint to persist in Firestore
        if (requestId) {
          await fetch('/api/userweb/assign-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: requestId, password: cleanPassword }),
          }).catch(() => {});
        }

        romanticAudio.playBloomSuccess();
        onSuccess('novio');
        return;
      } catch (err: any) {
        console.error('Error al asignar contraseña:', err);
        setError(err?.message || 'Error al guardar la contraseña.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Standard Login flow when password is already assigned
    setIsSubmitting(true);
    try {
      const entered = passwordInput.trim();
      // Check against assigned password
      if (assignedPassword && entered === assignedPassword.trim()) {
        romanticAudio.playBloomSuccess();
        onSuccess('novio');
        return;
      }

      // Check with Firebase Auth in case they already have account credentials
      const fbResult = await signInAdmin(targetEmail, entered);
      if (fbResult.success && fbResult.user) {
        romanticAudio.playBloomSuccess();
        onSuccess(fbResult.detectedProfile || 'novio');
        return;
      }

      setError('Contraseña incorrecta. Por favor verifica o asigna una nueva contraseña.');
      romanticAudio.playChime();
    } catch (err: any) {
      setError(err?.message || 'Error de verificación de acceso.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md rounded-3xl bg-white border border-[#F0DDE4] p-6 sm:p-7 shadow-[0_20px_60px_rgba(232,93,142,0.18)] relative text-left space-y-4 text-[#302B30]"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full text-[#77717A] hover:text-[#302B30] bg-[#FFF4F7] hover:bg-[#FFE8EF] transition-colors cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#FFF4F7] text-[#E85D8E] border border-[#F0DDE4] flex items-center justify-center shadow-inner shrink-0">
            {isAssigningPassword ? <KeyRound className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#E85D8E]">
                {isAssigningPassword ? 'Asignar Contraseña' : 'Seguridad & Acceso'}
              </span>
              <span className="px-1.5 py-0.2 rounded-full text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-300">
                {orderCode ? `#${orderCode}` : 'Acceso Seguro'}
              </span>
            </div>
            <h3 className="font-bold text-base sm:text-lg text-[#302B30] leading-tight">
              {isAssigningPassword
                ? 'Asigna tu Contraseña de Acceso'
                : (coupleTitle ? `Panel de ${coupleTitle}` : 'Acceso Administrativo')}
            </h3>
          </div>
        </div>

        <p className="text-xs text-[#77717A] leading-relaxed">
          {isAssigningPassword
            ? 'Asigna una contraseña de acceso para administrar las fotos, cartas y detalles de tu sitio web.'
            : 'Ingresa con tu correo registrado y tu contraseña para acceder al panel de administración.'}
        </p>

        {/* Registered Email Badge */}
        <div className="p-3 bg-[#FFF4F7] rounded-xl border border-[#F0DDE4] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#E85D8E] shrink-0" />
            <div className="min-w-0">
              <span className="text-[10px] text-[#77717A] uppercase font-bold block">
                Correo registrado
              </span>
              <span className="text-xs font-semibold text-[#302B30] truncate block">
                {registeredEmail || 'tucorreo@ejemplo.com'}
              </span>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 shrink-0">
            Autorizado
          </span>
        </div>

        {/* Form */}
        <form onSubmit={handleCredentialsSubmit} className="space-y-3.5 pt-1 text-xs">
          {!registeredEmail && (
            <div>
              <label className="block text-[#302B30] font-semibold mb-1 text-[11px]">
                Correo Electrónico
              </label>
              <div className="relative">
                <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#77717A]" />
                <input
                  type="email"
                  required
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="Introduce tu correo electrónico..."
                  className="w-full pl-9 pr-3.5 py-2.5 bg-[#FFFDFB] border border-[#F0DDE4] rounded-xl text-[#302B30] placeholder-[#77717A]/60 focus:outline-none focus:border-[#E85D8E] focus:ring-1 focus:ring-[#E85D8E] text-xs transition-colors"
                  autoComplete="email"
                />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[#302B30] font-semibold text-[11px]">
                {isAssigningPassword ? 'Nueva Contraseña' : 'Contraseña de Acceso'}
              </label>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-[10px] text-[#E85D8E] hover:text-[#DF4D80] font-medium cursor-pointer"
              >
                {showPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            <div className="relative">
              <KeyRound className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#77717A]" />
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  if (error) setError('');
                }}
                placeholder="Mínimo 6 caracteres..."
                className="w-full pl-9 pr-3.5 py-2.5 bg-[#FFFDFB] border border-[#F0DDE4] rounded-xl text-[#302B30] placeholder-[#77717A]/60 focus:outline-none focus:border-[#E85D8E] focus:ring-1 focus:ring-[#E85D8E] text-xs font-mono transition-colors"
              />
            </div>
          </div>

          {isAssigningPassword && (
            <div>
              <label className="block text-[#302B30] font-semibold text-[11px] mb-1">
                Confirmar Contraseña
              </label>
              <div className="relative">
                <KeyRound className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#77717A]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={confirmPasswordInput}
                  onChange={(e) => {
                    setConfirmPasswordInput(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="Repite tu contraseña..."
                  className="w-full pl-9 pr-3.5 py-2.5 bg-[#FFFDFB] border border-[#F0DDE4] rounded-xl text-[#302B30] placeholder-[#77717A]/60 focus:outline-none focus:border-[#E85D8E] focus:ring-1 focus:ring-[#E85D8E] text-xs font-mono transition-colors"
                />
              </div>
            </div>
          )}

          {/* Toggle between Assign and Login if already has password */}
          {hasExistingPassword && (
            <div className="flex justify-end pt-0.5">
              <button
                type="button"
                onClick={() => {
                  setIsAssigningPassword(!isAssigningPassword);
                  setError('');
                }}
                className="text-[11px] text-[#E85D8E] hover:text-[#DF4D80] hover:underline cursor-pointer transition-colors"
              >
                {isAssigningPassword
                  ? 'Ya tengo una contraseña, iniciar sesión'
                  : '¿Deseas cambiar o reasignar tu contraseña?'}
              </button>
            </div>
          )}

          {/* Error Banner */}
          {error && (
            <div className="p-2.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-[11px] flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 text-rose-500" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 shadow-md shadow-[#E85D8E]/20 transition-all cursor-pointer disabled:opacity-50 bg-gradient-to-r from-[#E85D8E] to-[#FF8DAF] hover:from-[#DF4D80] hover:to-[#FF7A9F] text-white"
          >
            <ShieldCheck className="w-4 h-4" />
            <span>
              {isSubmitting
                ? 'Guardando y verificando…'
                : isAssigningPassword
                ? 'Asignar Contraseña y Acceder'
                : 'Entrar a mi Panel Admin'}
            </span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </form>

        {/* Security Footer */}
        <div className="pt-2 border-t border-[#F0DDE4] flex items-center justify-center gap-1.5 text-[10px] text-[#77717A]">
          <Shield className="w-3 h-3 text-emerald-500" />
          <span>Acceso privado protegido para tu web</span>
        </div>
      </motion.div>
    </div>
  );
};
